import { User } from "firebase/auth";
import { useUpdateUser } from "@hooks/trpc/useMe";
import { Href, router } from "expo-router";
import { useLandingStore } from "store/useLandingStore";

import { AuthStatus } from "types/auth";
import {
  CustomActionLink,
  DirectLink,
  HandoffLink,
  LoginLink,
} from "types/branch";
import { AppState, Platform } from "react-native";
import { applyReferral } from "@utils/links/referral";

export const generateLoginLinkAction = (params: LoginLink) => {
  return async (status: AuthStatus): Promise<boolean> => {
    console.log("BRANCH:: login-link", {
      email: params.email,
      hasPassword: Boolean(params.password),
    });

    if (status === "logged-in") {
      return true;
    }

    if (status === "no-user") {
      router.navigate({
        pathname: "/login",
        params: {
          prefilledEmail: params.email,
          prefilledPassword: params.password,
        },
      });

      return true;
    }

    return false;
  };
};

export const generateHandoffLinkAction = (params: HandoffLink) => {
  return async (status: AuthStatus): Promise<boolean> => {
    // Never log params: `h` is an exchangeable sign-in credential.
    console.log("BRANCH:: handoff-link", { hasToken: Boolean(params.h) });

    if (!params.h) {
      // Nothing to exchange: a signed-in app is already where the link
      // wanted it; a signed-out one has no way in from here.
      return status === "logged-in";
    }

    if (status === "logged-in" || status === "no-user") {
      // Hand off to the /auth/handoff receiver rather than exchanging here.
      // That route is the ONE implementation of this flow: it shows the
      // sign-in UI and — crucially — routes through postAuthDestination
      // afterwards, so migrated users land in onboarding instead of being
      // dropped on home like an ordinary login. Doing the exchange inline
      // here (as this action used to) silently skipped all of that. A
      // signed-in app goes too: the receiver keeps the session when the
      // token names the same person and switches when it does not — the
      // fan proved that identity on the web seconds ago.
      router.replace({
        pathname: "/auth/handoff",
        params: params.next
          ? { h: params.h, next: params.next }
          : { h: params.h },
      });
      return true;
    }

    return false;
  };
};

export const generateDirectLinkAction = (params: DirectLink) => {
  return async (status: AuthStatus): Promise<boolean> => {
    console.log("BRANCH:: direct-link", params);

    if (status !== "logged-in" && Platform.OS !== "web") {
      return false;
    }

    // On web, check if we're already on the target path to avoid duplicate navigation
    // This happens when pasting a Branch link - the browser navigates first, then Branch processes it
    if (Platform.OS === "web") {
      const currentPath = window.location.pathname;
      const targetPath = params.url;

      if (currentPath === targetPath) {
        console.log("BRANCH:: already on target path, skipping navigation", {
          currentPath,
          targetPath,
        });
        return true;
      }
    }

    // Allow a small amount of time for any current screen to gain focus
    // before we navigate to the link.
    await new Promise((resolve) => setTimeout(resolve, 50));

    console.log("BRANCH::navigating to:", params.url, {
      appState: AppState.currentState,
    });

    /**
     * withAnchor (used to avoid default behavior of router overriding the initialRouteName)
     *   True: You want the initialRouteName to load.
     *   False: You do not want the initialRouteName to load.
     */

    useLandingStore.setState({ deepLink: params.url as Href });
    console.log("BRANCH::deepLink set in store", {
      url: params.url,
      appState: AppState.currentState,
    });

    return true;
  };
};

export const generateCustomActionLinkAction = (
  params: CustomActionLink,
  updateUser: ReturnType<typeof useUpdateUser>,
) => {
  return async (
    status: AuthStatus,
    user: User | undefined,
  ): Promise<boolean> => {
    if (status !== "logged-in" || user === undefined) {
      return false;
    }

    switch (params.type) {
      case "refer-friend": {
        console.log("BRANCH:: refer-friend", params.userId);
        await applyReferral({
          referrerId: params.userId,
          userId: user.uid,
          updateUser,
        });
        break;
      }
      default: {
        console.error("Invalid custom-action-link type");
      }
    }

    return true;
  };
};

export type LinkAction = (
  status: AuthStatus,
  user: User | undefined,
) => Promise<boolean>;
